import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { getBackers } from "@/lib/api";
import { fmtUSD, fmtNum } from "@/lib/format";
import { Crown, Trophy } from "lucide-react";
import SeoContent from "@/components/SeoContent";

const RANGES = [
  { key: "all", label: "All Time" },
  { key: "24h", label: "24H" },
  { key: "7d", label: "7D" },
  { key: "30d", label: "30D" },
];

export default function Backers() {
  const [range, setRange] = useState("all");
  const [backers, setBackers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getBackers(range).then((d) => { setBackers(d); setLoading(false); }).catch(() => setLoading(false));
  }, [range]);

  return (
    <div className="min-h-screen grain">
      <Navbar />
      <div className="mx-auto max-w-3xl px-4 py-10">
        <div className="flex items-center gap-3">
          <Trophy className="h-7 w-7 text-gold" strokeWidth={1.5} />
          <h1 className="font-display text-4xl font-black uppercase tracking-tighter">Top Backers</h1>
        </div>
        <p className="mt-2 text-zinc-400">Wallets ranked by total USD contributed across FLIP.</p>

        <div className="mt-6 flex rounded-full border border-white/10 bg-white/[0.02] p-1">
          {RANGES.map((r) => (
            <button
              key={r.key}
              data-testid={`backers-range-${r.key}`}
              onClick={() => setRange(r.key)}
              className={`rounded-full px-4 py-1.5 text-xs font-bold uppercase tracking-wide transition-colors ${
                range === r.key ? "bg-white text-black" : "text-zinc-500 hover:text-white"
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>

        <div className="mt-6 overflow-hidden rounded-2xl border border-white/10 bg-[#080808]" data-testid="backers-table">
          {loading ? (
            <div className="p-6 space-y-2">{[...Array(8)].map((_, i) => <div key={i} className="h-12 animate-pulse rounded bg-white/[0.02]" />)}</div>
          ) : backers.map((b) => (
            <Link to={`/user/${b.wallet_address}`} key={b.wallet_address} className="flex items-center justify-between border-b border-white/5 px-5 py-4 hover:bg-white/[0.03]" data-testid={`backer-row-${b.rank}`}>
              <div className="flex items-center gap-4">
                <span className={`flex items-center gap-1 font-mono text-lg font-black ${b.rank === 1 ? "text-gold" : "text-zinc-500"}`}>
                  {b.rank === 1 && <Crown className="h-4 w-4 fill-gold" strokeWidth={1.5} />}{b.rank}
                </span>
                <div>
                  <div className="font-mono text-sm text-zinc-100">
                    {b.is_house_incentive ? (
                      <span title="Platform's publicly declared Incentive Fund">🏦 FLIP Incentive Fund</span>
                    ) : (b.ens || b.wallet_short)}
                  </div>
                  <div className="text-[11px] text-zinc-600">{fmtNum(b.contributions)} contributions</div>
                </div>
              </div>
              <span className="font-mono text-lg font-bold">{fmtUSD(b.total_usd)}</span>
            </Link>
          ))}
          {!loading && backers.length === 0 && <div className="px-4 py-12 text-center text-zinc-600">No backers in this window yet</div>}
        </div>
      </div>
      <SeoContent variant="backers" />
    </div>
  );
}
